"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InsertResolutionResult = exports.InsertScheduleUpdate = exports.InsertScheduleCreate = exports.InsertScheduleSummary = void 0;
const zod_1 = require("zod");
const enums_js_1 = require("./enums.js");
// Mirror of the DB CHECK constraints in InsertSchedule:
//   (mode = 'interval') = (interval_seconds IS NOT NULL)
//   end_at IS NULL OR end_at > start_at
function applyInsertRefinements(value, ctx) {
    const isInterval = value.mode === 'interval';
    const hasInterval = value.interval_seconds != null;
    if (isInterval && !hasInterval) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: "mode='interval' requires interval_seconds",
            path: ['interval_seconds'],
        });
    }
    if (!isInterval && hasInterval) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: "mode='always' must not set interval_seconds",
            path: ['interval_seconds'],
        });
    }
    if (value.end_at != null) {
        const start = Date.parse(value.start_at);
        const end = Date.parse(value.end_at);
        if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: 'end_at must be strictly after start_at',
                path: ['end_at'],
            });
        }
    }
}
exports.InsertScheduleSummary = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    tenant_id: zod_1.z.string().uuid(),
    composer_id: zod_1.z.string().uuid(),
    scope_group_id: zod_1.z.string().uuid().nullable(),
    start_at: zod_1.z.string().datetime(),
    end_at: zod_1.z.string().datetime().nullable(),
    mode: zod_1.z.enum(enums_js_1.InsertModes),
    interval_seconds: zod_1.z.number().int().positive().nullable(),
    max_repetitions: zod_1.z.number().int().positive().nullable(),
    priority: zod_1.z.number().int().nonnegative(),
    is_emergency: zod_1.z.boolean(),
    created_by_id: zod_1.z.string().uuid(),
    created_at: zod_1.z.string().datetime(),
});
exports.InsertScheduleCreate = zod_1.z
    .object({
    composer_id: zod_1.z.string().uuid(),
    scope_group_id: zod_1.z.string().uuid().nullable().default(null),
    start_at: zod_1.z.string().datetime(),
    end_at: zod_1.z.string().datetime().nullable().default(null),
    mode: zod_1.z.enum(enums_js_1.InsertModes),
    interval_seconds: zod_1.z.number().int().positive().nullable().default(null),
    max_repetitions: zod_1.z.number().int().positive().nullable().default(null),
    priority: zod_1.z.number().int().nonnegative().default(100),
    is_emergency: zod_1.z.boolean().default(false),
})
    .superRefine(applyInsertRefinements);
exports.InsertScheduleUpdate = zod_1.z
    .object({
    scope_group_id: zod_1.z.string().uuid().nullable().optional(),
    start_at: zod_1.z.string().datetime().optional(),
    end_at: zod_1.z.string().datetime().nullable().optional(),
    mode: zod_1.z.enum(enums_js_1.InsertModes).optional(),
    interval_seconds: zod_1.z.number().int().positive().nullable().optional(),
    max_repetitions: zod_1.z.number().int().positive().nullable().optional(),
    priority: zod_1.z.number().int().nonnegative().optional(),
    is_emergency: zod_1.z.boolean().optional(),
})
    .superRefine((value, ctx) => {
    // The four temporal fields are an ATOMIC unit: present-all or
    // present-none. A partial temporal patch is ambiguous against the
    // DB CHECK constraints, so reject it at the contract boundary.
    const temporalKeys = ['start_at', 'end_at', 'mode', 'interval_seconds'];
    const present = temporalKeys.filter((k) => value[k] !== undefined);
    if (present.length === 0)
        return; // non-temporal-only patch — fine
    if (present.length < temporalKeys.length) {
        for (const k of temporalKeys) {
            if (value[k] === undefined) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    path: [k],
                    message: 'temporal fields (start_at, end_at, mode, interval_seconds) must be updated together',
                });
            }
        }
        return;
    }
    // All four present — apply the existing cross-field invariants.
    applyInsertRefinements({
        start_at: value.start_at,
        end_at: value.end_at,
        mode: value.mode,
        interval_seconds: value.interval_seconds,
    }, ctx);
});
// Heartbeat-response delta the server returns when the player's current
// assignment needs to change (insert is now active, demand is fired server-side,
// override is in force until a deadline). The player applies this patch to its
// local assignment state.
exports.InsertResolutionResult = zod_1.z.object({
    assignment_delta: zod_1.z.object({
        prepend_composer_id: zod_1.z.string().uuid().optional(),
        insert_schedule_id: zod_1.z.string().uuid().optional(),
        override_until: zod_1.z.string().datetime().optional(),
    }),
});
//# sourceMappingURL=insert.schema.js.map